import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "../../componentes/PageHeader";
import "./ckeditor-content.css";
import "../../styles/circuit-background.css";
import GradientBanner from "../../componentes/Banner";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDate } from "../../utils/dateFormatter";
import { motion } from "framer-motion";
import { API_BASE_URL } from '../../api/config';
import { incrementNewsView } from '../../api/news';
import {
  faNewspaper,
  faTags,
  faBookmark,
  faClipboardList,
  faLayerGroup,
  faSpinner,
  faCalendarAlt,
  faArrowLeft,
  faExclamationTriangle,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";

const baseUrl = API_BASE_URL;

// View tracking constants
const VIEW_TTL_MS = 30 * 60 * 1000; // 30 minutes

const getViewStorageKey = (slug) => `news:viewed:${slug}`;

const shouldIncrementView = (slug) => {
  if (!slug) return false;
  try {
    const key = getViewStorageKey(slug);
    const raw = localStorage.getItem(key);
    const lastSeen = raw ? Number(raw) : 0;
    if (Number.isFinite(lastSeen) && lastSeen > 0) {
      const age = Date.now() - lastSeen;
      if (age >= 0 && age < VIEW_TTL_MS) {
        return false;
      }
    }
    return true;
  } catch {
    return true;
  }
};

const markViewAttempt = (slug) => {
  if (!slug) return;
  try {
    localStorage.setItem(getViewStorageKey(slug), Date.now().toString());
  } catch {
    // ignore storage failures
  }
};

const VideoEmbed = ({ url }) => {
  const getEmbedUrl = (url) => {
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.includes("youtube.com")
        ? url.split("v=")[1]?.split("&")[0]
        : url.split("youtu.be/")[1]?.split("?")[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`;
    } else if (url.includes("vimeo.com")) {
      const videoId = url.split("vimeo.com/")[1]?.split("?")[0];
      return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1`;
    }
    return url;
  };

  return (
    <div className="aspect-video mb-8">
      <iframe
        src={getEmbedUrl(url)}
        className="w-full h-full rounded-xl shadow-lg"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Embedded video"
      />
    </div>
  );
};

const NewsDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [newsItem, setNewsItem] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewCount, setViewCount] = useState(0);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const viewIncrementedRef = useRef(false);

  // Fetch article data
  useEffect(() => {
    viewIncrementedRef.current = false; // Reset on slug change

    const params = new URLSearchParams();
    params.append("lang", lang || "en");

    fetch(`${baseUrl}/api/news/${slug}?${params.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error("News item not found");
        return res.json();
      })
      .then((data) => {
        setNewsItem(data);
        setViewCount(data.views || 0);
        setLoading(false);

        if (data.category) {
          fetch(
            `${baseUrl}/api/news?category=${data.category.slug}&limit=10&${params.toString()}`
          )
            .then((res) => res.json())
            .then((data) => {
              const relatedNewsData = Array.isArray(data) ? data : data.results || [];
              const filteredRelatedNews = relatedNewsData.filter((news) =>
                String(news.slug) !== String(slug)
              );
              setRelatedNews(filteredRelatedNews.slice(0, 3));
            })
            .catch((err) => console.error("Error fetching related news:", err));
        }
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug, lang]);

  // Increment view count (separate effect to avoid race conditions)
  useEffect(() => {
    if (!newsItem || viewIncrementedRef.current) return;
    if (!shouldIncrementView(slug)) return;

    viewIncrementedRef.current = true;
    markViewAttempt(slug);

    incrementNewsView(slug).then((result) => {
      if (result && typeof result.views === 'number') {
        setViewCount(result.views);
      }
    });
  }, [newsItem, slug]);

  if (loading) {
    return (
      <>
        <PageHeader />
        <FullscreenSpinner />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title="News Not Found"
          description=""
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("header.news"), link: "/news" }
          ]}
          backgroundIcons={[faNewspaper, faTags, faBookmark]}
        />
        <div className="container mx-auto px-4 py-16 text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-300 text-6xl mb-4" />
          <h2 className="font-catamaran text-2xl font-bold text-gray-700 mb-4">Article Not Found</h2>
          <p className="font-nunito-sans text-gray-500 mb-8">The news article you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate("/news")}
            className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-nunito-sans"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back to News
          </button>
        </div>
      </>
    );
  }

  if (!newsItem) return null;

  const hasMedia = newsItem.image_file || newsItem.image || (newsItem.media_type === "video" && (newsItem.video_file || newsItem.video)) || (newsItem.image && (newsItem.image.includes('youtube.com') || newsItem.image.includes('youtu.be') || newsItem.image.includes('vimeo.com')));

  return (
    <>
      <PageHeader />
      <div className="max-xl:pl-4 bg-gradient-to-r from-midnigt to-shake mt-[72px] pt-8 sm:pt-8 xl:pt-8 px-dynamic min-h-[14rem] md:min-h-[15rem] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                opacity: 0.1,
              }}
            >
              <FontAwesomeIcon
                icon={[faNewspaper, faTags, faBookmark, faClipboardList, faLayerGroup][i % 5]}
                className="text-white text-4xl"
              />
            </div>
          ))}
        </div>

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2 text-white/80 text-sm md:mb-8 mb-6 max-xl:mt-8"
          >
            <button onClick={() => navigate("/")} className="hover:text-white transition-colors">
              {t("header.home")}
            </button>
            <span>/</span>
            <button onClick={() => navigate("/news")} className="hover:text-white transition-colors">
              {t("header.news")}
            </button>
            <span>/</span>
            <span className="break-words">
              {newsItem.title}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-h-5 font-bold text-white tracking-tight font-catamaran break-words max-w-[90%] md:max-w-[80%] mb-4 md:mb-6"
            >
              {newsItem.category?.name || t("header.news")}
            </motion.h1>
          </motion.div>
        </div>
      </div>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                {/* Article Header */}
                <div className="p-8 pb-6">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 font-catamaran mb-6 leading-tight">
                    {newsItem.title}
                  </h1>
                  <div className="flex items-center gap-4 mb-6 flex-wrap">
                    <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                      <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2 text-primary" />
                      {formatDate(newsItem.created_at, lang)}
                    </span>
                    <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                      <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-2 text-primary" />
                      {viewCount.toLocaleString()} {viewCount === 1 ? t("news.view", "view") : t("news.views", "views")}
                    </span>
                    <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium font-nunito-sans">
                      {newsItem.category?.name || "Uncategorized"}
                    </span>
                  </div>
                </div>

                {/* Media Section */}
                {hasMedia && (
                  <div className="px-6 lg:px-8 mb-8">
                    {newsItem.media_type === "video" || (newsItem.image && (newsItem.image.includes('youtube.com') || newsItem.image.includes('youtu.be') || newsItem.image.includes('vimeo.com'))) ? (
                      newsItem.video_file ? (
                        <video
                          src={newsItem.video_file}
                          className="w-full aspect-video object-cover rounded-xl shadow-lg"
                          autoPlay
                          muted
                          loop
                          controls
                        />
                      ) : newsItem.video ? (
                        <VideoEmbed url={newsItem.video} />
                      ) : (newsItem.image && (newsItem.image.includes('youtube.com') || newsItem.image.includes('youtu.be') || newsItem.image.includes('vimeo.com'))) ? (
                        <VideoEmbed url={newsItem.image} />
                      ) : null
                    ) : (
                      <div className="aspect-video overflow-hidden rounded-xl shadow-lg">
                        <img
                          src={newsItem.image_file || newsItem.image}
                          alt={newsItem.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Article Content */}
                <div className="px-6 lg:px-8 pb-8">
                  <div
                    dangerouslySetInnerHTML={{ __html: newsItem.description }}
                    className="rich-content"
                  />
                </div>
              </motion.article>
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-[28rem] flex-shrink-0">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-lg p-6 sticky top-8"
              >
                <h3 className="font-catamaran text-xl font-bold text-gray-900 mb-6">
                  {t("news.related_news")}
                </h3>

                {relatedNews.length > 0 ? (
                  <div className="space-y-4">
                    {relatedNews.map((news) => (
                      <motion.div
                        key={news.id}
                        whileHover={{ y: -2 }}
                        className="group cursor-pointer p-4 rounded-xl border border-gray-100 hover:border-primary/30 hover:shadow-md transition-all duration-300"
                        onClick={() => {
                          navigate(`/news/${news.slug}`);
                          window.scrollTo(0, 0);
                        }}
                      >
                        {(news.image_file || news.image) && (
                          <div className="aspect-video overflow-hidden rounded-lg mb-3">
                            <img
                              src={news.image_file || news.image}
                              alt={news.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}

                        <h3 className="font-catamaran font-semibold text-gray-900 group-hover:text-primary line-clamp-2 mb-2 transition-colors">
                          {news.title}
                        </h3>

                        <div className="flex items-center text-xs text-gray-500 font-nunito-sans">
                          <FontAwesomeIcon icon={faCalendarAlt} className="w-3 h-3 mr-1" />
                          {formatDate(news.created_at, lang)}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FontAwesomeIcon icon={faNewspaper} className="text-gray-300 text-3xl mb-3" />
                    <p className="font-nunito-sans text-gray-500 text-sm">{t("news.no_related_news")}</p>
                  </div>
                )}


              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewsDetail;